/**
 * bayu-collection-products.js
 * Handles AJAX filtering, sorting, layout switching, quick-add,
 * and image slider for the Bayu Collection Products section.
 *
 * Written as plain functions — no classes.
 */

(function () {
  'use strict';

  // ─── Constants ─────────────────────────────────────────────────
  var SECTION_TYPE = 'bayu-collection-products';
  var PRICE_DEBOUNCE = 600;   // ms to wait before applying price range

  // ─── State ─────────────────────────────────────────────────────
  var sectionId;
  var priceDebounceTimer;
  var isLoading = false;

  // Cached element references (refreshed after each AJAX update)
  var refs = {};

  // ─── Boot ──────────────────────────────────────────────────────
  function init() {
    var section = document.querySelector('[data-section-type="' + SECTION_TYPE + '"]');
    if (!section) return;

    sectionId = section.dataset.sectionId;
    cacheRefs();
    bindEvents();
    initAllSliders();
    initPriceSliders();
  }

  // ─── Element cache ─────────────────────────────────────────────
  function cacheRefs() {
    refs.section = document.querySelector('[data-section-type="' + SECTION_TYPE + '"]');
    const asideFilterSection = document.getElementById('bayu-cp-aside-' + sectionId);
    refs.aside = asideFilterSection;
    refs.filterForm = asideFilterSection.querySelector('#bayu-cp-filter-form-' + sectionId);
    refs.availToggle = asideFilterSection.querySelector('.bayu-cp__toggle-input');
    refs.loader = document.getElementById('bayu-cp-loader-' + sectionId);
    refs.gridWrap = document.getElementById('bayu-cp-grid-wrap-' + sectionId);
    refs.chips = document.getElementById('bayu-cp-chips-' + sectionId);
    refs.count = document.getElementById('bayu-cp-count-' + sectionId);
    refs.viewBtns = document.getElementById('bayu-cp-view-btns-' + sectionId);
    refs.sortDesktop = document.getElementById('bayu-cp-sort-' + sectionId);

    const filterDrawerMobile = document.querySelector('.bayu-cp__filter-mobile-drawer');
    refs.drawerFilterForm = filterDrawerMobile.querySelector('#bayu-cp-filter-form-' + sectionId);
    refs.drawerAvailToggle = filterDrawerMobile.querySelector('.bayu-cp__toggle-input');
    refs.drawerClose = document.getElementById('bayu-cp-drawer-close-' + sectionId);
    refs.drawerApply = document.getElementById('bayu-cp-drawer-apply-' + sectionId);
    refs.drawerClear = document.getElementById('bayu-cp-drawer-clear-' + sectionId);
    refs.mobileFilterBtn = document.getElementById('bayu-cp-mobile-filter-btn-' + sectionId);
    refs.mobileSortSelect = document.getElementById('bayu-cp-mobile-sort-' + sectionId);
    refs.mobileViewBtn = document.getElementById('bayu-cp-mobile-view-btn-' + sectionId);
    refs.overlay = document.getElementById('bayu-cp-overlay-' + sectionId);
  }

  // ─── Event binding ─────────────────────────────────────────────
  function bindEvents() {
    // Checkbox filter changes
    if (refs.filterForm) {
      refs.filterForm.addEventListener('change', function (e) {
        if (e.target.classList.contains('bayu-cp__filter-checkbox')) {
          // Immediate pill visual feedback
          var pill = e.target.closest('.bayu-cp__pill');
          if (pill) {
            pill.classList.toggle('is-active', e.target.checked);
          }
          // Update accordion count badge (handles both pills and toggle switches)
          updateAccordionCount(e.target);
          applyFilters();
        }
      });
    }

    // availability toggle changes
    if (refs.availToggle) {
      refs.availToggle.addEventListener('change', function (e) {
        const parent = e.target.closest('.bayu-cp__toggle-group')
        const inStockInput = parent.querySelector('.bayu-cp__filter-availability-hidden[value="1"]');
        const outOfStockInput = parent.querySelector('.bayu-cp__filter-availability-hidden[value="0"]');

        // by changing inStockInput and outOfStockInput, we are changing value in form
        if (e.target.checked) {
          inStockInput.checked = true;
          outOfStockInput.checked = false;
        } else {
          inStockInput.checked = false;
          outOfStockInput.checked = true;
        }
        applyFilters();
      });
    }

    // Checkbox filter changes
    if (refs.drawerFilterForm) {
      refs.drawerFilterForm.addEventListener('change', function (e) {
        if (e.target.classList.contains('bayu-cp__filter-checkbox')) {
          // Immediate pill visual feedback
          var pill = e.target.closest('.bayu-cp__pill');
          if (pill) {
            pill.classList.toggle('is-active', e.target.checked);
          }
          // Update accordion count badge (handles both pills and toggle switches)
          updateAccordionCount(e.target);
        }
      });
    }

    // availability toggle changes
    if (refs.drawerAvailToggle) {
      refs.drawerAvailToggle.addEventListener('change', function (e) {
        const parent = e.target.closest('.bayu-cp__toggle-group')
        const inStockInput = parent.querySelector('.bayu-cp__filter-availability-hidden[value="1"]');
        const outOfStockInput = parent.querySelector('.bayu-cp__filter-availability-hidden[value="0"]');

        // by changing inStockInput and outOfStockInput, we are changing value in form
        if (e.target.checked) {
          inStockInput.checked = true;
          outOfStockInput.checked = false;
        } else {
          inStockInput.checked = false;
          outOfStockInput.checked = true;
        }
      });
    }

    // Desktop sort
    if (refs.sortDesktop) {
      refs.sortDesktop.addEventListener('change', applyFilters);
    }

    // Delegated clicks on the entire section
    if (refs.section) {
      refs.section.addEventListener('click', handleSectionClick);
    }

    // Layout view buttons
    if (refs.viewBtns) {
      refs.viewBtns.addEventListener('click', function (e) {
        var btn = e.target.closest('.bayu-cp__view-btn');
        if (btn) handleLayoutSwitch(btn);
      });
    }

    // Mobile: filter drawer open
    if (refs.mobileFilterBtn) {
      refs.mobileFilterBtn.addEventListener('click', openMobileFilters);
    }

    // Mobile: overlay backdrop click closes drawer (ignore clicks inside the drawer)
    if (refs.overlay) {
      refs.overlay.addEventListener('click', function (e) {
        if (e.target === refs.overlay) closeMobileFilters();
      });
    }

    // Drawer: close button
    if (refs.drawerClose) {
      refs.drawerClose.addEventListener('click', closeMobileFilters);
    }

    // Drawer: apply button — close drawer (filters already applied live)
    if (refs.drawerApply) {
      refs.drawerApply.addEventListener('click', () => {
        closeMobileFilters();
        applyMobileFilters();
      });
    }

    // Drawer: clear button — navigate to base collection URL
    if (refs.drawerClear) {
      refs.drawerClear.addEventListener('click', function () {
        var url = refs.drawerClear.dataset.removeUrl;
        if (url) fetchResults(url, true);
      });
    }

    // Mobile: sort select change
    if (refs.mobileSortSelect) {
      refs.mobileSortSelect.addEventListener('change', function () {
        applyMobileFilters();
      });
    }

    // Mobile: view toggle (2-col ↔ 1-col)
    if (refs.mobileViewBtn) {
      refs.mobileViewBtn.addEventListener('click', handleMobileViewToggle);
    }

    // Browser back/forward
    window.addEventListener('popstate', function () {
      fetchResults(window.location.href, false);
    });
  }

  // ─── Delegated click handler ────────────────────────────────────
  function handleSectionClick(e) {
    // Active filter chip removal
    var chip = e.target.closest('.bayu-cp__chip');
    if (chip && chip.dataset.removeUrl) {
      e.preventDefault();
      fetchResults(chip.dataset.removeUrl, true);
      return;
    }

    // Size swatch — add variant to cart if not disabled
    var sizeVariantSwatch = e.target.closest('.bayu-cp__swatch');
    if (sizeVariantSwatch) {
      if (!sizeVariantSwatch.classList.contains('bayu-cp__swatch--disabled')) {
        handleQuickAdd(sizeVariantSwatch);
      }
      return;
    }

    // Image slider dot
    var dot = e.target.closest('.bayu-cp__dot');
    if (dot) {
      var card = dot.closest('.bayu-cp__card');
      if (card) goToSlide(card, parseInt(dot.dataset.index, 10));
      return;
    }

    // Circular quick-add button
    var circleBtn = e.target.closest('.bayu-cp__quick-add-circle');
    if (circleBtn && !circleBtn.disabled) {
      e.preventDefault();
      e.stopPropagation();
      handleQuickAddCircle(circleBtn);
      return;
    }

    // Wishlist toggle
    var wishlistBtn = e.target.closest('.bayu-cp__wishlist');
    if (wishlistBtn) {
      e.preventDefault();
      e.stopPropagation();
      wishlistBtn.classList.toggle('is-wished');
      return;
    }
  }

  // ─── Update accordion count badge immediately (pills + toggles) ──
  function updateAccordionCount(element) {
    var accordion = element.closest('.bayu-cp__accordion');
    if (!accordion) return;

    var activePills = accordion.querySelectorAll('.bayu-cp__pill.is-active');
    var count = activePills.length;
    var summary = accordion.querySelector('.bayu-cp__accordion-head');
    var badge = summary && summary.querySelector('.bayu-cp__accordion-count');

    if (count > 0) {
      if (badge) {
        badge.textContent = count;
        badge.removeAttribute('style');
      }
    } else if (badge) {
      badge.style.display = 'none';
    }
  }

  // ─── Filter application ─────────────────────────────────────────
  function applyFilters() {
    var params = new URLSearchParams();

    // Collect active filter values from the form when filters are present
    if (refs.filterForm) {
      var formData = new FormData(refs.filterForm);
      formData.forEach(function (value, key) {
        if (value !== '') params.append(key, value);
      });
    }

    var sortValue = (refs.sortDesktop && refs.sortDesktop.value);
    if (sortValue) params.set('sort_by', sortValue);

    var qs = params.toString();
    var url = window.location.pathname + (qs ? '?' + qs : '');
    fetchResults(url, true);
  }

  function applyMobileFilters() {
    var params = new URLSearchParams();

    // Collect active filter values from the form when filters are present
    if (refs.drawerFilterForm) {
      var formData = new FormData(refs.drawerFilterForm);
      formData.forEach(function (value, key) {
        if (value !== '') params.append(key, value);
      });
    }

    var sortValue = (refs.mobileSortSelect && refs.mobileSortSelect.value);
    if (sortValue) params.set('sort_by', sortValue);

    var qs = params.toString();
    var url = window.location.pathname + (qs ? '?' + qs : '');
    fetchResults(url, true);
  }

  // ─── AJAX fetch & DOM update ────────────────────────────────────
  function fetchResults(url, pushState) {
    if (isLoading) return;

    // Use the actual runtime section ID for the Sections Rendering API
    var sep = url.includes('?') ? '&' : '?';
    var fetchUrl = url + sep + 'sections=' + sectionId;

    showLoader();

    fetch(fetchUrl, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Fetch failed: ' + response.status);
        return response.json();
      })
      .then(function (data) {
        // Try runtime section ID first; fall back to type name for legacy setups
        var sectionHtml = data[sectionId] || data[SECTION_TYPE];
        if (!sectionHtml) throw new Error('Section not found in response.');

        var parser = new DOMParser();
        var doc = parser.parseFromString(sectionHtml, 'text/html');

        // general element update
        updateElement('bayu-cp-grid-wrap-' + sectionId, doc);
        // desktop element update
        var asideAccordionState = captureAsideAccordionState();
        updateElement('bayu-cp-aside-' + sectionId, doc);
        updateElement('bayu-cp-chips-' + sectionId, doc);
        updateElement('bayu-cp-sort-' + sectionId, doc);
        updateElementText('bayu-cp-count-' + sectionId, doc);
        restoreAsideAccordionState(asideAccordionState);
        // mobile element update
        updateElement('bayu-cp-overlay-' + sectionId, doc);
        updateElement('bayu-cp-drawer-chips-' + sectionId, doc);
        updateElement('bayu-cp-mobile-sort-' + sectionId, doc);

        // Re-cache refs, then re-bind all elements.
        cacheRefs();
        bindEvents();
        initAllSliders();
        initPriceSliders();

        // Preserve current column count after AJAX re-render
        if (currentLayout && refs.section) {
          refs.section.style.setProperty('--bayu-cp-columns', currentLayout);
        }

        if (pushState) {
          history.pushState({ bayuFilter: true }, '', url);
        }
      })
      .catch(function (err) {
        console.error('[bayu-collection-products] AJAX error:', err);
      })
      .finally(function () {
        hideLoader();
      });
  }

  // Replace an element's innerHTML from the fetched document
  function updateElement(id, doc) {
    var freshEl = doc.getElementById(id);
    var currentEl = document.getElementById(id);
    if (freshEl && currentEl) {
      currentEl.innerHTML = freshEl.innerHTML;
    }
  }

  // Snapshot open/closed state of every accordion in the desktop aside,
  // keyed by data-filter-index so it survives an innerHTML replacement.
  function captureAsideAccordionState() {
    var states = {};
    var aside = document.getElementById('bayu-cp-aside-' + sectionId);
    if (!aside) return states;
    var accordions = aside.querySelectorAll('.bayu-cp__accordion');
    accordions.forEach(function (acc) {
      var key = acc.dataset.filterIndex;
      if (key && !isNaN(Number(key))) states[key] = acc.hasAttribute('open');
    });
    return states;
  }

  function restoreAsideAccordionState(states) {
    if (!states) return;
    var aside = document.getElementById('bayu-cp-aside-' + sectionId);
    if (!aside) return;
    var accordions = aside.querySelectorAll('.bayu-cp__accordion');
    accordions.forEach(function (acc) {
      var key = acc.dataset.filterIndex;
      if (key && !isNaN(Number(key))) {
        if (states[key]) {
          acc.setAttribute('open', '');
        } else {
          acc.removeAttribute('open');
        }
      }
    });
  }

  // Replace only the text content of an element
  function updateElementText(id, doc) {
    var freshEl = doc.getElementById(id);
    var currentEl = document.getElementById(id);
    if (freshEl && currentEl) {
      currentEl.textContent = freshEl.textContent;
    }
  }

  // ─── Loading overlay ────────────────────────────────────────────
  function showLoader() {
    isLoading = true;
    var loader = document.getElementById('bayu-cp-loader-' + sectionId);
    if (loader) {
      loader.classList.add('is-loading');
      loader.removeAttribute('aria-hidden');
    }
  }

  function hideLoader() {
    isLoading = false;
    var loader = document.getElementById('bayu-cp-loader-' + sectionId);
    if (loader) {
      loader.classList.remove('is-loading');
      loader.setAttribute('aria-hidden', 'true');
    }
  }

  // ─── Mobile filter drawer ───────────────────────────────────────
  function openMobileFilters() {
    if (refs.overlay) refs.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeMobileFilters() {
    if (refs.overlay) refs.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  // ─── Mobile view toggle (2-col ↔ 1-col) ────────────────────────
  var mobileColumns = 2;

  function handleMobileViewToggle() {
    mobileColumns = mobileColumns === 2 ? 1 : 2;
    if (refs.section) {
      refs.section.style.setProperty('--bayu-cp-columns-mobile', mobileColumns);
    }
    if (refs.mobileViewBtn) {
      var icon = refs.mobileViewBtn.querySelector('.bayu-cp__mtb-view-icon');
      if (icon) {
        icon.innerHTML = mobileColumns === 1
          ? '<rect x="1" y="1" width="14" height="6" rx="0.8" stroke="currentColor" stroke-width="1.4"/><rect x="1" y="9" width="14" height="6" rx="0.8" stroke="currentColor" stroke-width="1.4"/>'
          : '<rect x="1" y="1" width="6" height="6" rx="0.8" stroke="currentColor" stroke-width="1.4"/><rect x="9" y="1" width="6" height="6" rx="0.8" stroke="currentColor" stroke-width="1.4"/><rect x="1" y="9" width="6" height="6" rx="0.8" stroke="currentColor" stroke-width="1.4"/><rect x="9" y="9" width="6" height="6" rx="0.8" stroke="currentColor" stroke-width="1.4"/>';
      }
    }
  }

  // ─── Layout switcher (2 / 3 / 4 columns) ──────────────────────────────
  var currentLayout = 0; // 0 = use Liquid default

  function handleLayoutSwitch(btn) {
    var cols = parseInt(btn.dataset.layout, 10);
    if (cols === currentLayout) return;

    currentLayout = cols;

    var allBtns = btn.closest('.bayu-cp__view-btns').querySelectorAll('.bayu-cp__view-btn');
    allBtns.forEach(function (b) {
      b.classList.remove('is-active');
      b.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('is-active');
    btn.setAttribute('aria-pressed', 'true');

    if (refs.section) {
      refs.section.style.setProperty('--bayu-cp-columns', cols);
    }
  }

  // ─── Quick add to cart ──────────────────────────────────────────
  function handleQuickAdd(btn) {
    // Swatch click — add variant directly without altering swatch label
    var swatchVariantId = btn.dataset.variantId;
    if (!swatchVariantId) return;
    btn.classList.add('bayu-cp__swatch--adding');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(swatchVariantId, 10), quantity: 1 })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { item_count: cart.item_count },
          bubbles: true
        }));
        btn.classList.remove('bayu-cp__swatch--adding');
        btn.classList.add('bayu-cp__swatch--added');
        setTimeout(function () { btn.classList.remove('bayu-cp__swatch--added'); }, 1600);
      })
      .catch(function () { btn.classList.remove('bayu-cp__swatch--adding'); });
    return;
  }

  // ─── Circular quick-add to cart ────────────────────────────────────
  function handleQuickAddCircle(btn) {
    var variantId = btn.dataset.variantId;
    if (!variantId) return;

    var originalHTML = btn.innerHTML;
    btn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
        return fetch('/cart.js').then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { item_count: cart.item_count },
          bubbles: true
        }));
        setTimeout(function () {
          btn.disabled = false;
          btn.innerHTML = originalHTML;
        }, 1600);
      })
      .catch(function () {
        btn.disabled = false;
        btn.innerHTML = originalHTML;
      });
  }

  // ─── Price range dual slider ─────────────────────────────────────

  function initPriceSliders() {
    var sliderEls = document.querySelectorAll(
      '.bayu-cp__price-slider'
    );
    sliderEls.forEach(function (sliderEl) {
      setupPriceSlider(sliderEl);
    });
  }

  function setupPriceSlider(sliderEl) {
    var minThumb = sliderEl.querySelector('.bayu-cp__slider-thumb--min');
    var maxThumb = sliderEl.querySelector('.bayu-cp__slider-thumb--max');
    var fill = sliderEl.querySelector('.bayu-cp__slider-fill');
    var tipMin = sliderEl.querySelector('.bayu-cp__slider-tooltip--min');
    var tipMax = sliderEl.querySelector('.bayu-cp__slider-tooltip--max');
    var hiddenMin = sliderEl.querySelector('.bayu-cp__price-hidden-min');
    var hiddenMax = sliderEl.querySelector('.bayu-cp__price-hidden-max');
    var rangeMax = parseFloat(sliderEl.dataset.max) || 100;

    if (!minThumb || !maxThumb) return;

    function pctOf(val) {
      return (parseFloat(val) / rangeMax) * 100;
    }

    function refreshFill() {
      var lo = pctOf(minThumb.value);
      var hi = pctOf(maxThumb.value);
      fill.style.left = lo + '%';
      fill.style.width = (hi - lo) + '%';
    }

    function positionTooltip(tooltipEl, thumbEl) {
      var pct = pctOf(thumbEl.value);
      // Correct for thumb radius so tip centres over the circle, not the track edge
      var thumbOffset = 10; // half of thumb width (20px)
      var trackWidth = sliderEl.querySelector('.bayu-cp__slider-track-outer').offsetWidth;
      var px = (pct / 100) * (trackWidth - thumbOffset * 2) + thumbOffset;
      tooltipEl.style.left = px + 'px';
      tooltipEl.querySelector('span').textContent = thumbEl.value;
    }

    function showTip(tooltipEl, thumbEl) {
      positionTooltip(tooltipEl, thumbEl);
      tooltipEl.classList.add('is-visible');
    }

    function hideTip(tooltipEl) {
      tooltipEl.classList.remove('is-visible');
    }

    function applyPrice() {
      var lo = parseFloat(minThumb.value);
      var hi = parseFloat(maxThumb.value);
      if (hiddenMin) hiddenMin.value = lo > 0 ? lo : '';
      if (hiddenMax) hiddenMax.value = hi < rangeMax ? hi : '';

      // auto apply filter in desktop aside filter section
      if(hiddenMin.closest('.bayu-cp__aside')) {
        applyFilters();
      }
    }

    // Min thumb
    minThumb.addEventListener('input', function () {
      var lo = parseFloat(minThumb.value);
      var hi = parseFloat(maxThumb.value);
      if (lo >= hi) { minThumb.value = hi - 1; }
      refreshFill();
      showTip(tipMin, minThumb);
    });

    minThumb.addEventListener('mouseup', function () { hideTip(tipMin); applyPrice(); });
    minThumb.addEventListener('touchend', function () { hideTip(tipMin); applyPrice(); });

    // Max thumb
    maxThumb.addEventListener('input', function () {
      var lo = parseFloat(minThumb.value);
      var hi = parseFloat(maxThumb.value);
      if (hi <= lo) { maxThumb.value = lo + 1; }
      refreshFill();
      showTip(tipMax, maxThumb);
    });

    maxThumb.addEventListener('mouseup', function () { hideTip(tipMax); applyPrice(); });
    maxThumb.addEventListener('touchend', function () { hideTip(tipMax); applyPrice(); });

    // Initial paint
    refreshFill();
  }

  // ─── Image slider ────────────────────────────────────────────────

  // Initialize sliders on all cards in the grid
  function initAllSliders() {
    var cards = document.querySelectorAll(
      '#bayu-cp-grid-' + sectionId + ' [data-slider]'
    );
    cards.forEach(function (slider) {
      var card = slider.closest('.bayu-cp__card');
      if (!card || card.dataset.sliderInit) return;
      card.dataset.sliderInit = 'true';

      var dots = card.querySelector('[data-dots]');
      if (dots) bindDots(card, dots);
    });
  }

  // Bind dot-click events to switch slides
  function bindDots(card, dotsContainer) {
    dotsContainer.addEventListener('click', function (e) {
      var dot = e.target.closest('.bayu-cp__dot');
      if (!dot) return;
      goToSlide(card, parseInt(dot.dataset.index, 10));
    });
  }

  // Switch to a specific slide index
  function goToSlide(card, index) {
    var slides = card.querySelectorAll('.bayu-cp__slide');
    var dots = card.querySelectorAll('.bayu-cp__dot');

    slides.forEach(function (slide, i) {
      slide.classList.toggle('is-active', i === index);
    });

    dots.forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === index);
    });
  }

  // Return index of the currently active slide
  function getActiveIndex(slides) {
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].classList.contains('is-active')) return i;
    }
    return 0;
  }

  // ─── Touch / swipe on slider ────────────────────────────────────
  function initSwipeOnSliders() {
    var sliders = document.querySelectorAll(
      '#bayu-cp-grid-' + sectionId + ' [data-slider]'
    );
    sliders.forEach(function (slider) {
      if (slider.dataset.swipeInit) return;
      slider.dataset.swipeInit = 'true';

      var startX = 0;

      slider.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
      }, { passive: true });

      slider.addEventListener('touchend', function (e) {
        var diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) < 40) return;

        var card = slider.closest('.bayu-cp__card');
        var slides = slider.querySelectorAll('.bayu-cp__slide');
        var current = getActiveIndex(slides);

        if (diff > 0) {
          // Swipe left → next
          goToSlide(card, (current + 1) % slides.length);
        } else {
          // Swipe right → prev
          goToSlide(card, (current - 1 + slides.length) % slides.length);
        }
      }, { passive: true });
    });
  }

  // ─── Keyboard: Escape closes mobile drawer ─────────────────────
  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Escape' || e.key === 'Esc') && refs.overlay && refs.overlay.classList.contains('is-open')) {
      closeMobileFilters();
    }
  });

  // ─── Shopify theme editor re-init ──────────────────────────────
  document.addEventListener('shopify:section:load', function (e) {
    var target = e.target;
    if (target && target.querySelector('[data-section-type="' + SECTION_TYPE + '"]')) {
      sectionId = null;
      isLoading = false;
      refs = {};
      init();
    }
  });

  // ─── Entry point ────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      initSwipeOnSliders();
      initPriceSliders();
    });
  } else {
    init();
    initSwipeOnSliders();
    initPriceSliders();
  }

})();
